import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {GameCenterContainerConfig} from "../game-center-container.config";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {BreadcrumbsManager} from "../../../core/managers/breadcrumbs/breadcrumbs-manager";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
import {computed, observable} from "mobx";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {GameCenterLinkParams} from "./game-center-link-params";
import {CompetitionIndexPageDescriptionHeaderModel} from "../../competition-container/pages/index-page/models/competition-index-page.description-header.model";
import {DescriptionHeaderCompetitionViewModel} from "../../../core/components/description-header/models/description-header.competition.view-model";
import type {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import type {BreadcrumbsItemModel} from "../../../core/managers/breadcrumbs/breadcrumbs.item-model";
import {EXTERNAL_AD_CONFIG} from "../../../apps/website/consts/external-ad-config";
import {AD_DISPLAYS} from "../../../core/consts";

@singleton
export class GameCenterContainerState extends BaseModuleState {
  @observable competition: CompetitionDomainModel;


  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, GameCenterContainerConfig, WebsiteModuleState, BreadcrumbsManager];

  constructor(navigationManager: NavigationManager, appStore: AppStore, loggerUtils: LoggerUtils, datetimeUtils: DatetimeUtils, device: DeviceViewModel, termsUtils: TermsUtils, config: GameCenterContainerConfig, websiteModuleState: WebsiteModuleState, breadcrumbsManager: BreadcrumbsManager) {
    super(navigationManager, appStore, loggerUtils, datetimeUtils, device, termsUtils, config);

    this.websiteModuleState = websiteModuleState;
    this.breadcrumbsManager = breadcrumbsManager;
  }

  get isPopulated(): Boolean {
    return this.competition;
  }

  @computed get gameCenterLinkParam() {
    return new GameCenterLinkParams(this.props.match.params.competitionKey, this.props.match.params.matchDetails, this.props.match.params.sportName);
  }

  @computed get gameId(): Number {
    return this.props.match.hashParams ? Number(this.props.match.hashParams.id) : null
  }

  @computed get matchupId(): Number {
    return this.gameCenterLinkParam.matchupId
  }

  @computed get competitionId(): Number {
    return this.gameCenterLinkParam.competitionId
  }

  @computed get competitionViewModel(): CompetitionViewModel {
    return this.competition ? new CompetitionViewModel(this.competition) : null
  }

  @computed get sportTypeId(): Number {
    return this.competition?.sport?.id;
  }

  @computed get page() {
    return this.currentLinkModel.params.page
  }

  @computed get shouldRenderSideBar() {
    return !this.device.isSmallView
  }

  @computed get descriptionHeader(): CompetitionIndexPageDescriptionHeaderModel {
    let descriptionHeader = null;

    if (this.competition) {
      const {title, description} = new CompetitionIndexPageDescriptionHeaderModel(this.competition, this.websiteModuleState);

      descriptionHeader = new DescriptionHeaderCompetitionViewModel(this.competition, title, description, this.websiteModuleState?.config);
    }

    return descriptionHeader;
  }

  @computed get breadcrumbsList(): Array<BreadcrumbsItemModel> {
    return this.breadcrumbsManager.list;
  }

  @computed get hasBreadcrumbs(): Boolean {
    return this.breadcrumbsList?.length > 1;
  }

  @computed get userCountryId(): Number {
    return this.appState.locale?.userCountryId;
  }

  @computed get adConfig(): Number {
    return !this.device.isSmallView ? EXTERNAL_AD_CONFIG.DESKTOP.MATCH.AD970X90 : null;
  }

  @computed get adMock(): Number {
    return this.appState.settings.config.adDisplay === AD_DISPLAYS.placeholders
  }

  @computed get shouldRenderDBAIframe(): Boolean {
    const isUserCountryIdValid: Boolean = this.appState.locale.userCountryId === 21;
    const shouldRenderDBAIframe: Boolean = !this.appState.settings.isWidget && isUserCountryIdValid;

    return shouldRenderDBAIframe;
  }

  @computed get shouldRenderAd(): Boolean {
    return this.appState.externalAdsModel.shouldRender && (this.appState.externalAdsModel.remoteConfigModel.adsPrioritize || !this.shouldRenderDBAIframe);
  }

  @computed get shouldRenderAdsBorders(): Boolean {
    return this.appState.shouldRenderAdsBorders
  }
}
